import jwt_decode from 'jwt-decode';
import { isObject } from 'lodash';
import type { EventObject } from 'xstate';

import { CDN_URL } from '@webapp/common/conf';
import { isPreview } from '@webapp/common/lib/const';
import { partsToSeconds } from '@webapp/common/lib/date';
import { LocalStorage } from '@webapp/common/lib/storage';
import { getUrlSearchParam } from '@webapp/common/lib/utils';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import type { PageModel, QuestionModel, SurveyStore, TokenData } from '@webapp/common/resources/entities';
import type { InfoDetailsModel } from '@webapp/common/resources/entities/info';
import type { IQuestionCategory, QuestionEntity } from '@webapp/common/resources/entities/question';
import { filterWithImages, QuestionType } from '@webapp/common/resources/survey';

import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import type { QuestionResponseActor } from '@webapp/survey/src/entities/response-machine';
import type { GroupMachineActor } from '@webapp/survey/src/machine/group-machine';
import type { QuestionActorRef, UploadedFile } from '@webapp/survey/src/machine/question-machine';

import type { BundleEntity } from '../entities/bundle';

import { surveyApi } from './api';
import { isOfflineMode } from './is-offline-mode';

export const getLkToken = (): string | undefined => {
    let lktoken;

    if (isPreview) {
        lktoken = getUrlSearchParam('lktoken') || undefined;
    }

    return lktoken;
};

const getBundleHash = (bundle: BundleEntity): string =>
    bundle.questions
        .map((q) => q.getSnapshot().context.id)
        .sort()
        .join('-');

export const shufflePages = (pages: Array<PageModel>): Array<PageModel> => {
    const fixedPages = pages.map((p) => (!p.randomFixed ? p : null));
    const shuffledPages = [...pages.filter((p) => p.randomFixed)];
    if (shuffledPages.length === 0) return fixedPages;
    for (let i = shuffledPages.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledPages[i], shuffledPages[j]] = [shuffledPages[j], shuffledPages[i]];
    }
    let i = 0;
    return fixedPages.map((p) => p || shuffledPages[i++]);
};

export const shuffleAnswers = <T extends GroupMachineActor | QuestionAnswerActor>(answers: Array<T>): Array<T> => {
    const fixedAnswers = answers.map((p) => (p.getSnapshot().context.pinned ? p : null));
    const shuffledAnswers = answers.filter((p) => !p.getSnapshot().context.pinned);
    if (shuffledAnswers.length === 0) return fixedAnswers;
    for (let i = shuffledAnswers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledAnswers[i], shuffledAnswers[j]] = [shuffledAnswers[j], shuffledAnswers[i]];
    }
    let i = 0;
    return fixedAnswers.map((p) => p || shuffledAnswers[i++]);
};

export const shuffleCategories = <T extends IQuestionCategory>(categories: Array<T>): Array<T> => {
    const fixedCategories = categories.map((p) => (p.pinned ? p : null));
    const shuffledCategories = categories.filter((p) => !p.pinned);
    if (shuffledCategories.length === 0) return fixedCategories;
    for (let i = shuffledCategories.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledCategories[i], shuffledCategories[j]] = [shuffledCategories[j], shuffledCategories[i]];
    }
    let i = 0;
    return fixedCategories.map((p) => p || shuffledCategories[i++]);
};

export const loadBundlesOrder = (domain: string, bundles: Array<BundleEntity>): Array<BundleEntity> => {
    const currentBundlesOrderHashed = bundles.map(getBundleHash);
    const currentBundlesByHash = currentBundlesOrderHashed.reduce((acc, hash, idx) => {
        acc[hash] = bundles[idx];
        return acc;
    }, {} as Record<string, BundleEntity>);

    const lsvalue = LocalStorage.get<{
        orderHashes: Array<string>;
        questionOrdersByHash: Record<number, string>;
    }>(`new_bundles_random_${domain}`);
    if (lsvalue) {
        try {
            const { orderHashes: oldBundlesOrder, questionOrdersByHash } = lsvalue;
            const oldBundlesOrderMapped = oldBundlesOrder.map((hash) => currentBundlesByHash[hash]).filter(Boolean);
            const isValid = oldBundlesOrderMapped.length === bundles.length;
            if (!isValid) {
                return bundles;
            }
            oldBundlesOrderMapped.forEach((bundle) => {
                const hash = getBundleHash(bundle);
                bundle.questions.forEach((q) => {
                    const order = questionOrdersByHash[hash][q.getSnapshot().context.id];
                    if (typeof order === 'number') {
                        q.send({ type: 'SET_ORDER', value: order });
                    }
                });
            });

            return oldBundlesOrderMapped;
        } catch (e) {
            console.error(e);
        }
    }
    return bundles;
};

export const storeBundlesOrder = (domain: string, bundles: Array<BundleEntity>): void => {
    const orderHashes = bundles.map(getBundleHash);
    const questionOrdersByHash = orderHashes.reduce((acc, hash, idx) => {
        acc[hash] = bundles[idx].questions.reduce((acc, item) => {
            const question = item.getSnapshot().context;
            acc[question.id] = question.order;
            return acc;
        }, {});
        return acc;
    }, {});

    LocalStorage.set(`new_bundles_random_${domain}`, JSON.stringify({ orderHashes, questionOrdersByHash }));
};

export const storePagesOrder = (domain: string, pages: Array<PageModel>): void => {
    const pagesOrder = pages.map((page) => page.id);
    LocalStorage.set(`pages_random_${domain}`, JSON.stringify(pagesOrder));
};

export const loadPagesOrder = (domain: string, pages: Array<PageModel>): Array<PageModel> => {
    const pagesOrder = LocalStorage.get<Array<number>>(`pages_random_${domain}`);

    if (pagesOrder && pages.length === pagesOrder.length && pages.every((page) => pagesOrder.includes(page.id))) {
        return pagesOrder.map((id) => pages.find((page) => page.id === id)).filter(Boolean);
    }

    return pages;
};

// При выключенном сохранении незавершённых перезагрузка страницы начинает
// опрос заново (новый response), поэтому таймеры в localStorage не сохраняем
// и не восстанавливаем: иначе таймер жил дольше самого ответа, и новый ответ
// мог мгновенно завершиться по «истёкшему» таймеру прошлой попытки.
let timersPersistenceEnabled = true;

export const setTimersPersistence = (enabled: boolean): void => {
    timersPersistenceEnabled = enabled;
};

export const loadTimer = (uniqueId: string): number | null => {
    if (!timersPersistenceEnabled) return null;
    const timerStartValue = LocalStorage.get<string>(`new_new_timer-${uniqueId}`);

    if (timerStartValue) {
        return parseInt(timerStartValue, 10);
    }

    return null;
};

export const saveTimer = (uniqueId: string, datetime: number): void => {
    if (!timersPersistenceEnabled) return;
    LocalStorage.set(`new_new_timer-${uniqueId}`, datetime.toString());
};

export const removeTimer = (uniqueId: string): void => LocalStorage.remove(`new_new_timer-${uniqueId}`);

// Маркер «таймер вопроса истёк». Сам ключ таймера при истечении удаляется
// (см. timer-machine), поэтому без маркера после перезагрузки страницы нельзя
// понять, что на вопрос нельзя возвращаться.
const EXPIRED_TIMER_PREFIX = 'new_new_timer_expired-';

export const markTimerExpired = (uniqueId: string): void => {
    if (!timersPersistenceEnabled) return;
    LocalStorage.set(`${EXPIRED_TIMER_PREFIX}${uniqueId}`, '1');
};

export const unmarkTimerExpired = (uniqueId: string): void => LocalStorage.remove(`${EXPIRED_TIMER_PREFIX}${uniqueId}`);

export const isTimerMarkedExpired = (uniqueId: string): boolean =>
    timersPersistenceEnabled && Boolean(LocalStorage.get(`${EXPIRED_TIMER_PREFIX}${uniqueId}`));

// Истёк ли таймер вопроса: по живому актору (текущая сессия), по маркеру
// (истёк раньше, в т.ч. в прошлой сессии) или по сохранённому времени старта
// (старые ключи без маркера).
export const isQuestionTimerExpired = (q: QuestionActorRef): boolean => {
    const context = q.getSnapshot().context;
    if (!context.params?.timer) return false;
    const uniqueId = `question-timer-${context.id}`;

    const timer = context.timer?.getSnapshot();
    if (timer && timer.context.duration * 1000 <= timer.context.timeAgo) {
        return true;
    }
    if (isTimerMarkedExpired(uniqueId)) {
        return true;
    }
    const savedStart = loadTimer(uniqueId);
    if (savedStart) {
        const duration = partsToSeconds(context.params.hours, context.params.minutes, context.params.seconds);
        return savedStart + duration * 1000 <= Date.now();
    }
    return false;
};

// Удаляет все сохранённые таймеры опроса: общий и таймеры вопросов.
// Вызов синхронный и без условий — иначе на мобильных (закрыли вкладку до
// отложенной очистки, упал запрос завершения) ключи остаются, и при повторном
// прохождении таймер не обнуляется.
export const clearAllTimers = (domain: string, surveyId: number | string): void => {
    removeTimer(domain);
    removeTimer(`survey_timer-${surveyId}`);
    unmarkTimerExpired(`survey_timer-${surveyId}`);

    try {
        Object.keys(localStorage).forEach((key) => {
            if (
                key.startsWith('new_new_timer-question-timer-') ||
                key.startsWith(`${EXPIRED_TIMER_PREFIX}question-timer-`)
            ) {
                localStorage.removeItem(key);
            }
        });
    } catch {
        // localStorage может быть недоступен (WebView с заблокированным хранилищем,
        // приватный режим): Object.keys(undefined) ронял переход xstate (FRONT-2A9J).
        // Чистить нечего — остальные таймеры удаляются guarded-обёрткой выше.
    }
};

// Ключи таймеров привязаны к опросу, а не к конкретному ответу: если прошлая
// попытка была брошена без завершения (закрыли вкладку), ключи остаются, и
// новый ответ мог мгновенно завершиться пустым по «истёкшему» таймеру чужой
// попытки. Поэтому запоминаем rid ответа-владельца сохранённых таймеров и при
// смене ответа (новый заход = новый response) чистим их. При перезагрузке
// страницы того же ответа rid не меняется — таймеры восстанавливаются.
const TIMER_RESPONSE_PREFIX = 'new_new_timer_response-';

export const clearTimersOfOtherResponse = (
    domain: string,
    surveyId: number | string,
    rid: string | number | null,
): void => {
    if (rid === null || rid === undefined) return;
    const key = `${TIMER_RESPONSE_PREFIX}${surveyId}` as const;
    const owner = LocalStorage.get<string | number>(key);
    if (owner !== null && String(owner) === String(rid)) return;
    clearAllTimers(domain, surveyId);
    LocalStorage.set(key, String(rid));
};

export interface SavedCompleteSurvey {
    promo_code: string;
    questions: Array<QuestionModel>;
    testScore: number;
    quotaDisqual: boolean;
}

export const loadCompleteSurvey = (domain: string): SavedCompleteSurvey =>
    LocalStorage.get(`new_survey_complete_${domain}`);

export const saveCompleteSurvey = (domain: string, data: SavedCompleteSurvey): void =>
    LocalStorage.set(`new_survey_complete_${domain}`, data);

export const authenticated = ({ tokenData }: { tokenData: SurveyStore['tokenData'] }): boolean => {
    const privateData = tokenData.private;

    return Boolean(
        tokenData &&
            (privateData === 0 || (isObject(privateData) && privateData?.password === 0 && privateData?.vkAuth === 0)),
    );
};
export const preloadImages = async (questions: Array<QuestionEntity>): Promise<void> => {
    const withImages = filterWithImages(questions) as Array<QuestionEntity>;

    if (!isOfflineMode() && withImages.length === 0) return;

    try {
        await caches.open('images').then(function (cache) {
            const url = withImages.reduce(
                (q, { answers }) =>
                    q.concat(
                        answers.reduce((acc, a) => {
                            const path = a?.file?.path;

                            if (path) {
                                acc.push(`${CDN_URL}${path}`);
                            }

                            return acc;
                        }, []),
                    ),
                [],
            );
            // Вопросы могут повторяться на разных страницах: addAll падает с
            // InvalidStateError на дублях URL, поэтому чистим список.
            const uniqueUrl: Array<string> = Array.from(new Set(url));

            if (uniqueUrl.length === 0) return;

            return cache.addAll(uniqueUrl);
        });
    } catch (e) {
        // Предзагрузка картинок — необязательная оптимизация: недоступный
        // CacheStorage (приватный режим Firefox и т.п.) или сетевые сбои здесь
        // штатны. Раньше был console.error, который шумел в Sentry
        // (FRONT-MME и группы addAll-дублей).
        console.warn('[survey] preloadImages failed', e);
    }
};

export const loadIndividualTries = (domain: string): number =>
    LocalStorage.get<number>(`${domain}-invalid-password_new`) || 0;

export const incIndividualTries = (domain: string): number => {
    const tries = 1 + loadIndividualTries(domain);
    LocalStorage.set(`${domain}-invalid-password_new`, tries);
    return tries;
};

// https://github.com/statelyai/xstate/discussions/1591#discussioncomment-111941
export function assertEventType<TE extends EventObject, TType extends TE['type']>(
    event: TE,
    eventType: TType | Array<TType>,
): asserts event is TE & { type: TType } {
    if (Array.isArray(eventType) ? !eventType.includes(event.type as TType) : event.type !== eventType) {
        throw new Error(`Invalid event: expected "${eventType}", got "${event.type}"`);
    }
}

export const parseTokenData = (token: string | null): TokenData | null => {
    let tokenData = null;

    if (token) {
        try {
            tokenData = jwt_decode<{ data: AnyObject }>(token).data;
        } catch (e) {
            console.error(e);
        }
    }

    return tokenData;
};

export const getQ = (q: QuestionActorRef) => {
    return q.getSnapshot().context;
};

const packResponse = (r: QuestionResponseActor) => {
    const context = r.getSnapshot().context;
    return {
        value: context.value,
        extra: context.extra,
        comment: context.comment,
    };
};
const packIncompleteAnswer = (q: QuestionAnswerActor) => {
    const context = q.getSnapshot().context;
    return {
        id: context.id,
        response: packResponse(context.response),
    };
};

const packGroup = (g: GroupMachineActor) => {
    const context = g.getSnapshot().context;
    return {
        id: context.id,
        responses: Object.keys(context.responses).reduce((acc, responseKey) => {
            const response = context.responses[responseKey];
            acc[responseKey] = packResponse(response);
            return acc;
        }, {} as Record<string, ReturnType<typeof packResponse>>),
    };
};
const packIncompleteQuestion = (q: QuestionActorRef) => {
    const context = q.getSnapshot().context;
    return {
        id: context.id,
        answers: context.answersActors.map(packIncompleteAnswer),
        response: packResponse(context.responseActor),
        groups: context.groupsActors.map(packGroup),
        files: context.files,
        clicks: context.clicks,
        showImageTimestamp: context.showImageTimestamp,
    };
};

export type SavedQuestionResponse = {
    value: string | number | boolean;
    extra: string | number | boolean;
    comment: string;
};
export type SavedQuestionAnswer = {
    id: number;
    response: SavedQuestionResponse;
};

export type SaveQuestionGroup = {
    id: number;
    responses: Record<string, SavedQuestionResponse>;
};
export type SavedQuestion = {
    id: number;
    answers: Array<SavedQuestionAnswer>;
    response: SavedQuestionResponse;
    groups: Array<SaveQuestionGroup>;
    files: Array<UploadedFile>;
    clicks: Array<{ x: number; y: number; timeInMs: number; answerId: number }>;
    showImageTimestamp?: number;
};

export const saveIncompleteAnswersOfSurvey = (surveyContext: SurveyStore) => {
    const { questions, domain } = surveyContext;
    const touchedQuestions = questions.filter((q) => getQ(q).dirty);
    // console.log('>>> touchedQuestions:', questions, touchedQuestions);

    if (touchedQuestions.length > 0) {
        const incompleteQuestions = touchedQuestions.map(packIncompleteQuestion);
        LocalStorage.set(`new_answers__${domain}`, incompleteQuestions);
        surveyApi.saveIncomplete(incompleteQuestions);
    }
};

export const fillQuestions = (questions: Array<QuestionActorRef>, details: Array<InfoDetailsModel>) => {
    if (!details) return;

    const questionsObj = details.reduce((acc, item) => {
        if (acc[item.question_id]) {
            acc[item.question_id].push(item);
        } else {
            acc[item.question_id] = [item];
        }

        return acc;
    }, {});

    questions.forEach((questionRaw) => {
        const question = questionRaw.getSnapshot().context;
        const findInSaved = questionsObj[question.id];
        const { type } = question;

        // FIXME: Стремно, что не через событие, но работает. А если через send, то нет(
        if (findInSaved) {
            // questionRaw.send({ type: 'TOUCH'})
            question.dirty = true;
            setTimeout(() => {
                questionRaw.send({ type: 'SET_DETAILS_LOADED' });
            }, 800);

            questionRaw.send({ type: 'SHOW' });
            if (findInSaved[0].cant_rate) {
                if (type === QuestionType.SMILE) {
                    // Как в компоненте: CHANGE в question-level response +
                    // QUESTION_RESPONSE_CHANGE без answerId — SmileApi.change
                    // сбросит все per-answer responses (SET false).
                    question.responseActor.send({ type: 'CHANGE', value: CANTRATE_ID, questionId: question.id });
                    questionRaw.send({
                        type: 'QUESTION_RESPONSE_CHANGE',
                        questionId: question.id,
                        value: CANTRATE_ID,
                    });
                    return;
                }

                if (type === QuestionType.SELECT_ONE_IMAGE || type === QuestionType.SELECT_FEW_IMAGE) {
                    // Как в компоненте: CANTRATE_ID и в question-level response,
                    // и в response первого ответа (второе триггерит api.change →
                    // SET false всем per-answer responses).
                    question.responseActor.send({ type: 'CHANGE', value: CANTRATE_ID, questionId: question.id });
                    question.answersActors?.[0]
                        ?.getSnapshot()
                        .context.response.send({ type: 'CHANGE', value: CANTRATE_ID, questionId: question.id });
                    return;
                }

                if (type === QuestionType.FIRST_CLICK) {
                    // Как в компоненте: CANTRATE_ID в question-level response +
                    // CLICK_IMAGE с cantRate — очистит clicks и поставит
                    // CANTRATE_ID в default-зону.
                    question.responseActor.send({ type: 'CHANGE', value: CANTRATE_ID, questionId: question.id });
                    questionRaw.send({ type: 'CLICK_IMAGE', cantRate: true });
                    return;
                }

                if (
                    type === QuestionType.NPS ||
                    type === QuestionType.SCALE ||
                    type === QuestionType.STAR ||
                    type === QuestionType.LONG_TEXT ||
                    type === QuestionType.SHORT_TEXT ||
                    type === QuestionType.TEXT_PERCEPTION
                ) {
                    question.responseActor.send({ type: 'CHANGE', value: CANTRATE_ID, questionId: question.id });
                    return;
                }
            }

            switch (type) {
                case QuestionType.NPS:
                case QuestionType.NUMBER:
                case QuestionType.STAR: {
                    question.responseActor.send({
                        type: 'CHANGE',
                        value: findInSaved[0].answer_int,
                        questionId: question.id,
                    });
                    questionRaw.send({
                        type: 'QUESTION_RESPONSE_CHANGE',
                        value: findInSaved[0].answer_int,
                        questionId: question.id,
                    });
                    return;
                }

                case QuestionType.CSI:
                case QuestionType.MATRIX_RATING:
                case QuestionType.RANK:
                case QuestionType.TEST_RANK:
                case QuestionType.DISTRIBUTE_SCALE: {
                    question.answersActors.forEach((answerActor) => {
                        const answersInSaved = findInSaved.filter(
                            (item) => item.answer_id === answerActor.getSnapshot().context.id,
                        );

                        if (answersInSaved[0]?.answer_int) {
                            answerActor.getSnapshot().context.response.send({
                                type: 'CHANGE',
                                value: answersInSaved[0]?.answer_int,
                                questionId: question.id,
                            });
                        }

                        if (answersInSaved[1]?.answer_int) {
                            answerActor
                                .getSnapshot()
                                .context.response.send({ type: 'CHANGE_EXTRA', value: findInSaved[1].answer_int });
                        }

                        if (answersInSaved[0]?.cant_rate) {
                            answerActor
                                .getSnapshot()
                                .context.response.send({ type: 'CHANGE', value: CANTRATE_ID, questionId: question.id });
                        }
                    });
                    return;
                }

                case QuestionType.ONE_OF_LIST:
                case QuestionType.FEW_OF_LIST:
                case QuestionType.SELECT_FEW_IMAGE:
                case QuestionType.SELECT_ONE_IMAGE:
                case QuestionType.TEST_ONE_OF_LIST:
                case QuestionType.TEST_FEW_OF_LIST: {
                    question.answersActors.forEach((answerActor) => {
                        const findAnswer = findInSaved.find(
                            (item) => item.answer_id === answerActor.getSnapshot().context.id,
                        );

                        if (findAnswer?.answer_bool) {
                            answerActor.getSnapshot().context.response.send({
                                type: 'CHANGE',
                                value: Boolean(findAnswer?.answer_bool),
                                questionId: question.id,
                            });
                        }

                        if (findAnswer?.answer_text) {
                            answerActor
                                .getSnapshot()
                                .context.response.send({ type: 'CHANGE_EXTRA', value: findAnswer?.answer_text });
                        }
                    });
                    return;
                }

                case QuestionType.NAME: {
                    question.groupsActors.forEach((groupActor) => {
                        const answerInSaved = findInSaved.find(
                            (item) => item.answer_group_id === groupActor.getSnapshot().context.id,
                        );

                        if (answerInSaved) {
                            groupActor.send({
                                type: 'CHANGE_RESPONSE_VALUE',
                                answerId: answerInSaved.answer_id,
                                value: answerInSaved.answer_str,
                            });
                        }
                    });
                    return;
                }

                case QuestionType.SEM_DIFF: {
                    question.groupsActors.forEach((groupActor) => {
                        const answerInSaved = findInSaved.find(
                            (item) => item.answer_group_id === groupActor.getSnapshot().context.id,
                        );

                        if (answerInSaved) {
                            groupActor.send({
                                type: 'CHANGE_RESPONSE_VALUE',
                                answerId: answerInSaved.answer_id,
                                value: true,
                            });
                        }
                    });
                    return;
                }

                case QuestionType.MATRIX_TEXT_ANSWER:
                case QuestionType.MATRIX_DROPDOWN_LIST: {
                    question.groupsActors.forEach((groupActor) => {
                        const groupsInSaved = findInSaved.filter(
                            (item) => item.answer_group_id === groupActor.getSnapshot().context.id,
                        );

                        question.answersActors.forEach((answerActor) => {
                            const findAnswer = groupsInSaved.find(
                                (item) => item.answer_id === answerActor.getSnapshot().context.id,
                            );

                            if (findAnswer?.cant_rate) {
                                // Как handleCantRate в matrix-layout: CANTRATE_ID
                                // в CHANGE_RESPONSE_VALUE группы.
                                groupActor.send({
                                    type: 'CHANGE_RESPONSE_VALUE',
                                    answerId: findAnswer.answer_id,
                                    value: CANTRATE_ID,
                                });
                                return;
                            }

                            if (findAnswer) {
                                groupActor.send({
                                    type: 'CHANGE_RESPONSE_VALUE',
                                    answerId: findAnswer.answer_id,
                                    value: findAnswer.answer_str,
                                });
                            }
                        });
                    });
                    return;
                }

                case QuestionType.MATRIX_SINGLE_ANSWER:
                case QuestionType.MATRIX_FEW_ANSWERS: {
                    question.groupsActors.forEach((groupActor) => {
                        const groupsInSaved = findInSaved.filter(
                            (item) => item.answer_group_id === groupActor.getSnapshot().context.id,
                        );

                        question.answersActors.forEach((answerActor) => {
                            const findAnswer = groupsInSaved.find(
                                (item) => item.answer_id === answerActor.getSnapshot().context.id,
                            );

                            if (findAnswer?.cant_rate) {
                                // Как handleCantRate в matrix-layout: CANTRATE_ID
                                // в CHANGE_RESPONSE_VALUE группы.
                                groupActor.send({
                                    type: 'CHANGE_RESPONSE_VALUE',
                                    answerId: findAnswer.answer_id,
                                    value: CANTRATE_ID,
                                });
                                return;
                            }

                            if (findAnswer) {
                                groupActor.send({
                                    type: 'CHANGE_RESPONSE_VALUE',
                                    answerId: findAnswer.answer_id,
                                    value: true,
                                });
                            }

                            if (findAnswer?.answer_str) {
                                groupActor.send({
                                    type: 'CHANGE_RESPONSE_EXTRA',
                                    answerId: findAnswer.answer_id,
                                    value: findAnswer?.answer_str,
                                });
                            }
                        });
                    });
                    return;
                }

                case QuestionType.DROPDOWN_LIST: {
                    question.responseActor.send({
                        type: 'CHANGE',
                        value: findInSaved[0].answer_id,
                        questionId: question.id,
                    });
                    return;
                }

                case QuestionType.SHORT_TEXT:
                case QuestionType.TEST_TEXT: {
                    question.responseActor.send({
                        type: 'CHANGE',
                        value: findInSaved[0].answer_str,
                        questionId: question.id,
                    });
                    return;
                }

                case QuestionType.LONG_TEXT: {
                    question.responseActor.send({
                        type: 'CHANGE',
                        value: findInSaved[0].answer_text,
                        questionId: question.id,
                    });
                    return;
                }

                case QuestionType.AGREEMENT: {
                    question.responseActor.send({
                        type: 'CHANGE',
                        value: Boolean(findInSaved[0].answer_bool),
                        questionId: question.id,
                    });
                    return;
                }

                case QuestionType.PHONE: {
                    question.answersActors.forEach((answerActor) => {
                        const answerInSaved = findInSaved.find(
                            (item) => item.answer_id === answerActor.getSnapshot().context.id,
                        );

                        if (answerInSaved) {
                            answerActor.getSnapshot().context.response.send({
                                type: 'CHANGE',
                                value: answerInSaved.answer_str,
                                questionId: question.id,
                            });
                            answerActor
                                .getSnapshot()
                                .context.response.send({ type: 'CHANGE_EXTRA', value: answerInSaved.answer_text });
                        }
                    });
                    return;
                }

                case QuestionType.RESIDENCE:
                case QuestionType.NESTED_LIST: {
                    question.answersActors.forEach((answerActor) => {
                        const answerInSaved = findInSaved.find(
                            (item) => item.answer_id === answerActor.getSnapshot().context.id,
                        );

                        if (answerInSaved) {
                            answerActor.getSnapshot().context.response.send({
                                type: 'CHANGE',
                                value: Number(answerInSaved.answer_str),
                                questionId: question.id,
                            });
                            answerActor
                                .getSnapshot()
                                .context.response.send({ type: 'CHANGE_EXTRA', value: answerInSaved.answer_text });
                        }
                    });
                    return;
                }

                case QuestionType.EMAIL:
                case QuestionType.DATETIME:
                case QuestionType.SMILE:
                case QuestionType.GROUP_FREE_ANSWERS: {
                    question.answersActors.forEach((answerActor) => {
                        const answerInSaved = findInSaved.find(
                            (item) => item.answer_id === answerActor.getSnapshot().context.id,
                        );

                        if (answerInSaved?.cant_rate) {
                            // Галочка «Не могу оценить» здесь per-answer:
                            // CANTRATE_ID в response конкретного ответа.
                            answerActor.getSnapshot().context.response.send({
                                type: 'CHANGE',
                                value: CANTRATE_ID,
                                questionId: question.id,
                            });
                            return;
                        }

                        if (answerInSaved) {
                            answerActor.getSnapshot().context.response.send({
                                type: 'CHANGE',
                                value: answerInSaved.answer_str,
                                questionId: question.id,
                            });
                        }
                    });
                    return;
                }

                case QuestionType.KANO: {
                    question.answersActors.forEach((answerActor) => {
                        const answerInSaved = findInSaved.find(
                            (item) => item.answer_id === answerActor.getSnapshot().context.id,
                        );

                        if (answerInSaved?.cant_rate) {
                            // Как handleCantRate в kano-row: CANTRATE_ID в value
                            // и сброс extra (dysfunctional).
                            answerActor.getSnapshot().context.response.send({
                                type: 'CHANGE',
                                value: CANTRATE_ID,
                                questionId: question.id,
                            });
                            answerActor.getSnapshot().context.response.send({ type: 'CHANGE_EXTRA', value: undefined });
                        }
                    });
                    return;
                }

                case QuestionType.SCALE: {
                    question.responseActor.send({
                        type: 'CHANGE',
                        value: findInSaved[0].answer_group_id,
                        questionId: question.id,
                    });
                    return;
                }
            }
        }
    });
};
